import { useState, useEffect, useMemo, useRef, useCallback, startTransition } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SEO } from '@/components/SEO';
import { useTutors } from '@/hooks/useTutors';
import { useHeroTutorCards } from '@/hooks/useHeroTutorCards';
import { sortTutorsByEvaluation } from '@/utils/tutorDataUtils';
import { useSavedTutors } from '@/hooks/useSavedTutors';
import { useBatchReviews } from '@/hooks/useBatchReviews';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Star, Heart, Clock, Globe, Video, Search, Filter, Users, BookOpen, Zap, Award, TrendingUp, Target, BarChart3, CheckCircle, ArrowRight, Play, Calendar, DollarSign, MapPin, MessageCircle, Share, Monitor, Headphones, Wallet, Info } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { pb } from '@/lib/pocketbase';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import BookingModal from '@/components/BookingModal';
import SendMessageModal from '@/components/SendMessageModal';
import SubjectSpecificFilters from '@/components/SubjectSpecificFilters';
import SubjectSelector from '@/components/SubjectSelector';
import SearchAnimation from '@/components/SearchAnimation';
import TutorCountryFlag from '@/components/TutorCountryFlag';
import { useSubjects } from '@/hooks/useSubjects';
import { useCurrencyFormat } from '@/hooks/useCurrencyFormat';
import { trackSearch } from '@/lib/metaPixel';


import RandomTutorsShowcase from '@/components/RandomTutorsShowcase';
import HeroTutorCards from '@/components/HeroTutorCards';
import PaymentMethodsModal from "@/components/PaymentMethodsModal";
import TutorPricingModal from "@/components/TutorPricingModal";

// Optimized batch rating component - no individual API calls
const TutorRating = ({ tutorId, reviewStats }: { tutorId: string; reviewStats: any }) => {
  const stats = reviewStats[tutorId] || { average: 5.0, count: 0 };
  const displayRating = stats.average;
  const reviewCount = stats.count;

  return (
    <div className="flex items-center justify-center gap-1 mb-3">
      <div className="flex">
        {[...Array(5)].map((_, i) => (
          <Star 
            key={i} 
            className={`h-4 w-4 transition-all duration-300 ${
              i < Math.floor(displayRating) ? 'text-yellow-400 fill-current' : 'text-gray-300'
            }`} 
          />
        ))}
      </div>
      <span className="text-sm font-medium ml-1">
        {displayRating.toFixed(1)}
      </span>
      <span className="text-xs text-gray-500">({reviewCount})</span>
    </div>
  );
};

const SpinningEarth = () => {
  return (
    <div className="relative w-32 h-32 mx-auto mb-8">
      <div className="w-full h-full rounded-full bg-gradient-to-br from-blue-400 via-green-400 to-blue-600 animate-spin shadow-2xl relative overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-4 h-6 bg-green-500 rounded-full opacity-80"></div>
        <div className="absolute top-1/2 right-1/3 w-3 h-4 bg-green-600 rounded-full opacity-70"></div>
        <div className="absolute bottom-1/3 left-1/2 w-5 h-3 bg-green-500 rounded-full opacity-60"></div>
        <div className="absolute top-1/3 right-1/4 w-2 h-5 bg-green-400 rounded-full opacity-90"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-pulse"></div>
      </div>
      <div className="absolute -inset-4 rounded-full border border-blue-200 animate-ping opacity-20"></div>
      <div className="absolute -inset-2 rounded-full border border-blue-300 animate-pulse opacity-30"></div>
    </div>
  );
};

const SearchStats = () => {
  const { tutors } = useTutors();
  const { subjects } = useSubjects();
  const [totalSubjectsCount, setTotalSubjectsCount] = useState(0);
  
  useEffect(() => {
    const fetchAllSubjectsCount = async () => {
      try {
        // Get unique subjects from all sources
        const allSubjectNames = new Set();
        
        // Add subjects from main subjects collection
        subjects.forEach(subject => allSubjectNames.add(subject.name));
        
        // Add subjects from all tutors' subject arrays (this is the main source)
        tutors.forEach(tutor => {
          if (tutor.subjects && Array.isArray(tutor.subjects)) {
            tutor.subjects.forEach(subject => allSubjectNames.add(subject));
          }
        });
        
        // Add approved subjects from approved_subjects collection
        try {
          const approvedSubjects = await pb.collection('approved_subjects').getList(1, 100, {
            filter: 'isActive = true'
          });
          approvedSubjects.items.forEach(subject => allSubjectNames.add(subject.name));
        } catch (error) {
          // Silently handle error
        }
        
        // Add subject requests
        try {
          const subjectRequests = await pb.collection('subject_requests').getList(1, 100, {
            filter: 'status = "approved" & isActive = true'
          });
          subjectRequests.items.forEach(subject => allSubjectNames.add(subject.subjectName));
        } catch (error) {
          // Silently handle error
        }
        
        setTotalSubjectsCount(allSubjectNames.size);
      } catch (error) {
        console.error('Error fetching subjects count:', error);
        // Fallback - count unique subjects from tutors only
        const tutorSubjects = new Set();
        tutors.forEach(tutor => {
          if (tutor.subjects && Array.isArray(tutor.subjects)) {
            tutor.subjects.forEach(subject => tutorSubjects.add(subject));
          }
        });
        setTotalSubjectsCount(tutorSubjects.size || 1);
      }
    };
    
    // Only run when we have tutors data
    if (tutors.length > 0) {
      fetchAllSubjectsCount();
    }
  }, [subjects, tutors]);
  
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
      <div className="text-center p-2 bg-blue-50 rounded-lg">
        <div className="text-sm font-bold text-blue-600">{tutors.length}+</div>
        <div className="text-xs text-gray-600">Expert Tutors</div>
      </div>
      <div className="text-center p-2 bg-green-50 rounded-lg">
        <div className="text-sm font-bold text-green-600">{totalSubjectsCount}+</div>
        <div className="text-xs text-gray-600">Subjects</div>
      </div>
      <div className="text-center p-2 bg-purple-50 rounded-lg">
        <div className="text-sm font-bold text-purple-600">24/7</div>
        <div className="text-xs text-gray-600">Availability</div>
      </div>
      <div className="text-center p-2 bg-orange-50 rounded-lg">
        <div className="text-sm font-bold text-orange-600">95%</div>
        <div className="text-xs text-gray-600">Success Rate</div>
      </div>
    </div>
  );
};

const Tutors = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { tutors, isLoading, searchTutors, refreshTutors } = useTutors();
  const { heroTutors } = useHeroTutorCards();
  const { toggleSavedTutor, isTutorSaved } = useSavedTutors();
  const { formatPrice, selectedCurrency, currencies: contextCurrencies } = useCurrencyFormat();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedTutorForPayment, setSelectedTutorForPayment] = useState<any>(null);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [selectedTutorForPricing, setSelectedTutorForPricing] = useState<any>(null);

  // Helper function to format price - converts from tutor's currency to selected display currency
  const formatTutorPrice = (price: number, tutorCurrency: string = 'USD', tutorObj?: any) => {
    if (!price) return '0';
    
    const tutorCurrencyData = contextCurrencies.find(c => c.code === tutorCurrency);
    
    // Convert tutor's price to USD first
    let priceInUSD = price;
    if (tutorCurrency !== 'USD' && tutorCurrencyData) {
      priceInUSD = price / tutorCurrencyData.rate;
    }
    
    // Use the context's formatPrice to ensure consistent conversion and rounding
    const formatted = formatPrice(priceInUSD);
    
    // For EGP, convert to Arabic numerals
    if (selectedCurrency.code === 'EGP') {
      return formatted.replace(/\d/g, (d) => '٠١٢٣٤٥٦٧٨٩'[parseInt(d)]);
    }
    
    return formatted;
  };

  // Helper function to get localized text based on selected currency
  const getLocalizedText = (text: string) => {
    if (selectedCurrency.code === 'EGP') {
      switch (text) {
        case '/hr':
          return '/ساعة';
        default:
          return text;
      }
    }
    return text;
  };
  const [selectedSubject, setSelectedSubject] = useState<string | null>(searchParams.get('subject') || null);
  const [selectedCountry, setSelectedCountry] = useState<string | null>(searchParams.get('country') || null);
  const [selectedTutorForBooking, setSelectedTutorForBooking] = useState<any>(null);
  const [selectedTutorForMessage, setSelectedTutorForMessage] = useState<any>(null);
  const [filteredTutors, setFilteredTutors] = useState([]);
  const [showAllTutors, setShowAllTutors] = useState(false);
  const [localCoaches, setLocalCoaches] = useState<{[key: string]: any}>({});
  const [randomSeed, setRandomSeed] = useState(Math.random()); // Stable random seed
  const [initialSixTutors, setInitialSixTutors] = useState<any[]>([]); // Store initial 6 tutors
  const [isInitialLoading, setIsInitialLoading] = useState(true); // Track initial load state
  const tutorResultsRef = useRef<HTMLDivElement>(null); // Ref for scrolling to results
  const hasAutoScrolledToResultsRef = useRef(false);
  
  // Pagination state
  const [displayedCount, setDisplayedCount] = useState(20);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // Fetch local coaches data for local service indicators
  useEffect(() => {
    const fetchLocalCoaches = async () => {
      try {
        // Fetch fitness coaches
        const fitnessCoaches = await pb.collection('local_fitness_coaches').getList(1, 200, {
          filter: 'isActive = true && isVerified = true',
          expand: 'tutorId'
        });
        
        // Fetch music coaches
        let musicCoaches = { items: [] };
        try {
          musicCoaches = await pb.collection('local_music_coaches').getList(1, 200, {
            filter: 'isActive = true && isVerified = true',
            expand: 'tutorId'
          });
        } catch (error) {
          console.log('Music coaches collection not found');
        }
        
        // Create mapping of tutor IDs to local service types
        const coachesMap: {[key: string]: any} = {};
        
        fitnessCoaches.items.forEach(coach => {
          if (coach.tutorId) {
            coachesMap[coach.tutorId] = { 
              ...coachesMap[coach.tutorId], 
              fitness: true,
              fitnessData: coach
            };
          }
        });
        
        musicCoaches.items.forEach(coach => {
          if (coach.tutorId) {
            coachesMap[coach.tutorId] = { 
              ...coachesMap[coach.tutorId], 
              music: true,
              musicData: coach
            };
          }
        });
        
        setLocalCoaches(coachesMap);
      } catch (error) {
        console.error('Error fetching local coaches:', error);
      }
    };

    fetchLocalCoaches();
  }, []);

  const scrollToResults = searchParams.get('scrollToResults') === 'true';

  // Single unified effect for loading and searching tutors
  useEffect(() => {
    const loadAndSearch = async () => {
      // Initial load if no tutors yet
      if (tutors.length === 0) {
        await refreshTutors();
      }

      // Perform search with current filters
      const results = await searchTutors({
        searchQuery,
        selectedSubject: selectedSubject === 'All Subjects' ? '' : (selectedSubject || ''),
        selectedCountry: selectedCountry || '',
        priceRange: '',
        availability: ''
      });
      startTransition(() => {
        setFilteredTutors(results);
        setIsInitialLoading(false); // Mark initial loading as complete
      });
    };

    loadAndSearch();
  }, [searchQuery, selectedSubject, selectedCountry]);

  // Auto-scroll to results when arriving from the filter modal (mobile-friendly)
  useEffect(() => {
    if (!scrollToResults) {
      hasAutoScrolledToResultsRef.current = false;
      return;
    }

    if (hasAutoScrolledToResultsRef.current) return;
    if (isLoading || isInitialLoading) return;

    // NOTE: displayTutors is defined later; we just need the element to exist
    if (!tutorResultsRef.current) return;

    hasAutoScrolledToResultsRef.current = true;

    // Wait for layout/paint
    requestAnimationFrame(() => {
      tutorResultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });

    // Remove the flag from the URL so refresh/back doesn't keep auto-scrolling
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('scrollToResults');
    navigate(`/tutors?${nextParams.toString()}`, { replace: true });
  }, [scrollToResults, isLoading, isInitialLoading, filteredTutors, searchParams, navigate]);

  const handleSearch = async () => {
    setIsInitialLoading(true); // Show loading during search
    
    // Track search event for Meta Pixel
    if (searchQuery && searchQuery.trim()) {
      trackSearch(searchQuery);
    }
    
    const results = await searchTutors({
      searchQuery,
      selectedSubject: selectedSubject === 'All Subjects' ? '' : (selectedSubject || ''),
      selectedCountry: selectedCountry || '',
      priceRange: '',
      availability: ''
    });
    startTransition(() => {
      setFilteredTutors(results);
      setShowAllTutors(false);
      setIsInitialLoading(false); // Hide loading after search
    });
  };

  const handleSubjectChange = (subject: string | null) => {
    startTransition(() => {
      setSelectedSubject(subject);
      setShowAllTutors(false);
    });
  };

  const handleClearFilters = async () => {
    startTransition(() => {
      setSearchQuery('');
      setSelectedSubject(null);
      setSelectedCountry(null);
      setShowAllTutors(false);
      setRandomSeed(Math.random());
      setFilteredTutors(tutors);
    });
  };

  const handleShowAllTutors = () => {
    startTransition(() => {
      setShowAllTutors(true);
    });
  };

  const getTutorName = (tutor: any) => {
    if (tutor.name && tutor.name.trim()) {
      return tutor.name;
    }
    if (tutor.expand?.user?.name) {
      return tutor.expand.user.name;
    }
    return 'Unknown Tutor';
  };

  const getProfilePictureUrl = (tutor: any) => {
    if (tutor.profilePicture && tutor.profilePicture.trim()) {
      const url = pb.files.getURL(tutor, tutor.profilePicture);
      return url;
    }
    
    if (tutor.profilePictureUrl && tutor.profilePictureUrl.trim()) {
      return tutor.profilePictureUrl;
    }
    
    return '';
  };

  // Get session preference info (like homepage)
  const getSessionPreferenceInfo = (preference: string) => {
    switch (preference) {
      case 'video_only':
        return { 
          icon: Video, 
          text: 'Video Only', 
          color: 'bg-blue-100 text-blue-800 border-blue-200' 
        };
      case 'chat_only':
        return { 
          icon: MessageCircle, 
          text: 'Chat Only', 
          color: 'bg-green-100 text-green-800 border-green-200' 
        };
      case 'both':
      default:
        return { 
          icon: Monitor, 
          text: 'Video & Chat', 
          color: 'bg-purple-100 text-purple-800 border-purple-200' 
        };
    }
  };


  // Check if tutor offers local services
  const hasLocalServices = (tutorId: string) => {
    return localCoaches[tutorId];
  };

  const getLocalServiceTypes = (tutorId: string) => {
    const coach = localCoaches[tutorId];
    if (!coach) return [];
    
    const services = [];
    if (coach.fitness) services.push('Fitness');
    if (coach.music) services.push('Music');
    return services;
  };

  const handleBookSession = (tutor: any) => {
    setSelectedTutorForBooking(tutor);
  };

  const handleViewLessons = (tutor: any) => {
    const baseUrl = `/tutor-lessons?tutorId=${tutor.id}`;
    const subjectParam = selectedSubject ? `&subject=${selectedSubject}` : '';
    navigate(`${baseUrl}${subjectParam}`);
  };

  const handleSendMessage = (tutor: any) => {
    setSelectedTutorForMessage(tutor);
  };

  const handleToggleSave = (tutor: any) => {
    toggleSavedTutor(tutor);
    toast({
      title: isTutorSaved(tutor.id) ? "Tutor Removed" : "Tutor Saved!",
      description: isTutorSaved(tutor.id) 
        ? "Tutor removed from your saved list" 
        : "Tutor added to your saved list",
      duration: 3000
    });
  };

  const handleTutorClick = (tutor: any) => {
    navigate(`/tutor/${tutor.id}`);
  };

  const handleShareTutor = async (tutor: any) => {
    const shareUrl = `https://tutornesta.com/tutor/${tutor.id}`;
    
    try {
      if (navigator.share && navigator.canShare) {
        const tutorName = getTutorName(tutor);
        const subjects = tutor.subjects?.slice(0, 3).join(', ') || 'Multiple subjects';
        const shareText = `Check out this amazing tutor: ${tutorName} - ${subjects} at ${formatTutorPrice(tutor.hourlyRate || 0, tutor.pricingCurrency, tutor)}/hr`;
        
        await navigator.share({
          title: `Tutor Recommendation: ${tutorName}`,
          text: shareText,
          url: shareUrl,
        });
      } else {
        throw new Error('Web Share API not available');
      }
    } catch (error) {
      // Fallback to clipboard
      try {
        await navigator.clipboard.writeText(shareUrl);
        toast({
          title: "Link Copied!",
          description: "Tutor profile link copied to clipboard.",
          duration: 3000
        });
      } catch (clipboardError) {
        toast({
          title: "Share Failed",
          description: "Unable to share or copy link. Please try again.",
          duration: 3000,
          variant: "destructive"
        });
      }
    }
  };

  // Paginated tutors display - sorted by evaluation, with hero tutors prioritized when matching filters
  const tutorsToShow = useMemo(() => {
    const baseTutors = filteredTutors.length > 0 ? filteredTutors : tutors;
    const sortedTutors = sortTutorsByEvaluation(baseTutors);
    
    // If we have filters applied, prioritize hero tutors that match
    const hasFilters = searchQuery || selectedSubject || selectedCountry;
    if (hasFilters && heroTutors.length > 0) {
      // Find hero tutors that are in the filtered results
      const matchingHeroTutorIds = heroTutors
        .filter(heroTutor => sortedTutors.some(t => t.id === heroTutor.id))
        .map(t => t.id);
      
      if (matchingHeroTutorIds.length > 0) {
        // Separate matching hero tutors and other tutors
        const heroMatches = sortedTutors.filter(t => matchingHeroTutorIds.includes(t.id));
        const otherTutors = sortedTutors.filter(t => !matchingHeroTutorIds.includes(t.id));
        
        // Return hero tutors first, then others
        return [...heroMatches, ...otherTutors];
      }
    }
    
    return sortedTutors;
  }, [filteredTutors, tutors, heroTutors, searchQuery, selectedSubject, selectedCountry]);
  
  const shouldShowLimited = !searchQuery && !selectedSubject && !selectedCountry && !showAllTutors;
  
  // Calculate and store initial 6 random tutors (prioritizing higher evaluations)
  useEffect(() => {
    if (shouldShowLimited && tutorsToShow.length > 6 && initialSixTutors.length === 0) {
      const seededRandom = (seed: number) => {
        const x = Math.sin(seed++) * 10000;
        return x - Math.floor(x);
      };
      
      // Take top tutors based on evaluation (already sorted), then randomize from that pool
      // Use top 20 tutors (or all if less than 20) to ensure we get high-quality tutors
      const topTutorsPool = tutorsToShow.slice(0, Math.min(20, tutorsToShow.length));
      
      const shuffled = [...topTutorsPool]
        .map((tutor, index) => ({ tutor, sort: seededRandom(randomSeed + index) }))
        .sort((a, b) => a.sort - b.sort)
        .map(({ tutor }) => tutor);
      
      setInitialSixTutors(shuffled.slice(0, 6));
    }
  }, [tutorsToShow, shouldShowLimited, randomSeed, initialSixTutors.length]);
  
  // Display logic: keep initial 6 when clicking "View All", add more after them
  const displayTutors = useMemo(() => {
    if (shouldShowLimited) {
      // Show initial 6 random tutors
      return initialSixTutors;
    }
    
    // When "View All" clicked or filtering - keep initial 6 and add more
    if (showAllTutors && initialSixTutors.length > 0) {
      // Get remaining tutors excluding the initial 6
      const remainingTutors = tutorsToShow.filter(
        t => !initialSixTutors.some(initial => initial.id === t.id)
      );
      
      // Return initial 6 + paginated remaining
      return [
        ...initialSixTutors,
        ...remainingTutors.slice(0, displayedCount - 6)
      ];
    }
    
    // When searching/filtering (not "View All") - paginate normally
    return tutorsToShow.slice(0, displayedCount);
  }, [tutorsToShow, shouldShowLimited, initialSixTutors, showAllTutors, displayedCount]);
  
  // Batch load reviews ONLY for visible tutors
  const visibleTutorIds = useMemo(() => displayTutors.map(t => t.id), [displayTutors]);
  const { reviewStats, isLoading: reviewsLoading } = useBatchReviews(visibleTutorIds);
  
  // Infinite scroll logic - only active when NOT showing limited view
  const loadMore = useCallback(() => {
    if (shouldShowLimited || isLoadingMore || displayedCount >= tutorsToShow.length) return;
    
    startTransition(() => {
      setIsLoadingMore(true);
      setTimeout(() => {
        setDisplayedCount(prev => Math.min(prev + 20, tutorsToShow.length));
        setIsLoadingMore(false);
      }, 300);
    });
  }, [shouldShowLimited, isLoadingMore, displayedCount, tutorsToShow.length]);
  
  // Intersection observer for infinite scroll
  useEffect(() => {
    if (shouldShowLimited) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore();
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );
    
    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }
    
    return () => observer.disconnect();
  }, [loadMore, shouldShowLimited]);
  
  // Reset displayed count and initial tutors when filters change
  useEffect(() => {
    setDisplayedCount(20);
    setInitialSixTutors([]); // Clear initial tutors when filters change
  }, [searchQuery, selectedSubject, selectedCountry]);

  return (
    <div className="min-h-screen bg-white overflow-x-hidden flex-1">
      <SEO 
        title="Find Expert Tutors Online | ابحث عن مدرسين خبراء - TutorNesta"
        description="Discover qualified online tutors for any subject | اكتشف مدرسين مؤهلين اونلاين لأي مادة. فلتر حسب السعر والموقع. احجز دروس تجريبية مع معلمين خبراء في مصر والعالم."
        canonicalUrl="https://tutornesta.com/tutors"
        keywords="find tutors, online teachers, مدرسين اونلاين, معلمين خصوصيين, دروس خصوصية, ابحث عن مدرس, مدرس في مصر"
      />
      <Navbar />
      
      {/* Hero Section with Search Animation */}
      <section className="pt-16 lg:pt-24 pb-4 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 relative overflow-hidden">
        {/* Hero Tutor Cards - inside hero section */}
        <HeroTutorCards tutors={tutors} />
        <div className="absolute inset-0 opacity-30 pointer-events-none" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.05'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}></div>
        
        <div className="container mx-auto px-4 text-center relative max-w-7xl">
          <div className="max-w-2xl mx-auto">
            <div className="mb-3">
              <SearchStats />
            </div>

            {/* Random Tutors Showcase */}
            <RandomTutorsShowcase tutors={tutors} isLoading={isLoading} />
          </div>
        </div>
      </section>

      {/* Advanced Search Section */}
      <section className="py-16 bg-white relative">
        <div className="container mx-auto px-4 max-w-7xl">
          <Card className="max-w-6xl mx-auto shadow-2xl border-0 bg-gradient-to-r from-white to-gray-50">
            <CardHeader className="text-center pb-8">
              <CardTitle className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">Advanced Tutor Search</CardTitle>
              <CardDescription className="text-base md:text-lg text-gray-600">
                Use our smart filters to find the perfect tutor match
              </CardDescription>
            </CardHeader>
            <CardContent className="px-4 md:px-8 pb-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <Search className="h-4 w-4" />
                    Search Query
                  </label>
                  <Input
                    placeholder="Search tutors, subjects, or skills..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    className="h-12 text-lg border-2 border-gray-200 focus:border-blue-500 transition-colors"
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <BookOpen className="h-4 w-4" />
                    Subject
                  </label>
                  <SubjectSelector
                    value={selectedSubject}
                    onValueChange={handleSubjectChange}
                    placeholder="Select subject..."
                  />
                </div>
              </div>
              
              <div className="text-center">
                <Button 
                  size="lg" 
                  onClick={handleSearch}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-8 md:px-16 py-4 rounded-full text-base md:text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                >
                  <Search className="mr-3 h-6 w-6" />
                  <span className="hidden sm:inline">Find Perfect Tutors</span>
                  <span className="sm:hidden">Search</span>
                  <Zap className="ml-3 h-6 w-6" />
                </Button>
              </div>
            </CardContent>
          </Card>
          
          {/* Subject-Specific Filters Section - Now within container */}
          <div className="mt-12">
            <SubjectSpecificFilters 
              selectedSubject={selectedSubject}
              onSubjectChange={handleSubjectChange}
            />
          </div>
        </div>
      </section>

      {/* Tutors Results Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 max-w-7xl">
          {/* Active Filters Indicator - Top of page */}
          {(selectedCountry || selectedSubject || searchQuery) && (
            <div className="flex flex-wrap justify-center gap-2 mb-8">
              {selectedCountry && (
                <Badge variant="secondary" className="text-sm flex items-center gap-2">
                  <MapPin className="h-3 w-3" />
                  Country: {selectedCountry.toUpperCase()}
                  <button 
                    onClick={() => setSelectedCountry(null)}
                    className="ml-1 hover:bg-gray-200 rounded-full p-0.5"
                  >
                    ×
                  </button>
                </Badge>
              )}
              {selectedSubject && (
                <Badge variant="secondary" className="text-sm flex items-center gap-2">
                  <BookOpen className="h-3 w-3" />
                  Subject: {selectedSubject}
                  <button 
                    onClick={() => setSelectedSubject(null)}
                    className="ml-1 hover:bg-gray-200 rounded-full p-0.5"
                  >
                    ×
                  </button>
                </Badge>
              )}
              {searchQuery && (
                <Badge variant="secondary" className="text-sm flex items-center gap-2">
                  <Search className="h-3 w-3" />
                  Search: "{searchQuery}"
                  <button 
                    onClick={() => setSearchQuery('')}
                    className="ml-1 hover:bg-gray-200 rounded-full p-0.5"
                  >
                    ×
                  </button>
                </Badge>
              )}
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleClearFilters}
                className="text-xs"
              >
                Clear All
              </Button>
            </div>
          )}
          
          {/* Load More for Featured View - At Top */}
          {shouldShowLimited && tutorsToShow.length > 6 && (
            <div className="flex justify-center mb-8">
              <Button 
                size="lg"
                onClick={handleShowAllTutors}
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-8 py-4 rounded-full text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
              >
                Load more
                <ArrowRight className="ml-3 h-6 w-6" />
              </Button>
            </div>
          )}
          
          {(isLoading || isInitialLoading) ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 max-w-7xl mx-auto">
              {Array.from({ length: 8 }).map((_, index) => (
                <Card key={index} className="bg-gradient-to-br from-blue-50/50 to-blue-100/60 overflow-hidden border border-blue-200/50 h-full">
                  <CardContent className="p-0 h-full flex flex-col">
                    {/* Photo skeleton */}
                    <div className="relative h-72 overflow-hidden bg-muted">
                      <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 animate-pulse" />
                    </div>
                    
                    {/* Content skeleton */}
                    <div className="p-4 flex-1 flex flex-col justify-between space-y-3">
                      {/* Name and country */}
                      <div className="space-y-2">
                        <div className="h-6 bg-muted rounded animate-pulse w-3/4" />
                        <div className="h-4 bg-muted rounded animate-pulse w-1/2" />
                      </div>
                      
                      {/* Rating */}
                      <div className="h-4 bg-muted rounded animate-pulse w-2/3 mx-auto" />
                      
                      {/* Subjects */}
                      <div className="flex flex-wrap gap-1">
                        <div className="h-6 bg-muted rounded-full animate-pulse w-16" />
                        <div className="h-6 bg-muted rounded-full animate-pulse w-20" />
                      </div>
                      
                      {/* Bio */}
                      <div className="space-y-1">
                        <div className="h-3 bg-muted rounded animate-pulse w-full" />
                        <div className="h-3 bg-muted rounded animate-pulse w-5/6" />
                      </div>
                      
                      {/* Buttons */}
                      <div className="flex gap-2 pt-2">
                        <div className="h-10 bg-muted rounded-lg animate-pulse flex-1" />
                        <div className="h-10 bg-muted rounded-lg animate-pulse w-24" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : displayTutors.length > 0 ? (
            <>
              <div ref={tutorResultsRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 max-w-7xl mx-auto">
                 {displayTutors.map((tutor) => {
                   const tutorImageUrl = getProfilePictureUrl(tutor);
                   // Force re-render when currency changes by including it in key
                   const tutorKey = `${tutor.id}-${selectedCurrency.code}`;

                   return (
                    <Card key={tutorKey} className="bg-gradient-to-br from-blue-50/50 to-blue-100/60 hover:shadow-2xl transition-all duration-500 group overflow-hidden border border-blue-200/50 backdrop-blur-sm h-full">
                    <CardContent className="p-0 h-full flex flex-col">
                      {/* Photo Section with Better Organized Overlays */}
                      <div className="relative h-72 overflow-hidden bg-gray-200">
                        {/* Blurred background layer */}
                        {tutorImageUrl && (
                          <img
                            src={tutorImageUrl}
                            alt=""
                            className="absolute inset-0 w-full h-full object-cover scale-105"
                            style={{ filter: 'blur(4px) brightness(0.8)' }}
                          />
                        )}
                        
                        {/* Main image layer */}
                        <div 
                          className={`relative w-full h-full transition-all duration-300 cursor-pointer hover:scale-105 ${
                            tutorImageUrl 
                              ? '' 
                              : 'bg-gradient-to-br from-blue-400 to-purple-500'
                          }`}
                          onClick={() => handleTutorClick(tutor)}
                          title="Click to view tutor details"
                        >
                          {tutorImageUrl ? (
                            <img
                              src={tutorImageUrl}
                              alt={getTutorName(tutor)}
                              className="w-full h-full object-cover scale-102"
                              style={{ objectPosition: 'center 20%' }}
                            />
                          ) : (
                            <div className="flex items-center justify-center h-full">
                              <Users className="h-16 w-16 text-white/80" />
                            </div>
                          )}
                          
                          {/* Gradient overlay */}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent pointer-events-none"></div>
                          
                          {/* Featured Badge - Top Left Corner */}
                          {tutor.isFeatured && (
                            <div className="absolute top-2 left-2 z-10">
                              <Badge className="text-xs px-2 py-1 bg-yellow-500/90 text-white font-bold shadow-sm border-0">
                                <Star className="h-3 w-3 mr-1 fill-current" />
                                Featured
                              </Badge>
                            </div>
                          )}

                          {/* Action Buttons - Top Right Corner (Smaller) */}
                          <div className="absolute top-2 right-2 flex gap-1 z-10">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 rounded-full bg-white/90 backdrop-blur shadow-sm hover:bg-white p-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleShareTutor(tutor);
                              }}
                              title="Share tutor"
                            >
                              <Share className="h-3 w-3 text-blue-500" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 rounded-full bg-white/90 backdrop-blur shadow-sm hover:bg-white p-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggleSave(tutor);
                              }}
                              title={isTutorSaved(tutor.id) ? "Remove from saved" : "Save tutor"}
                            >
                              <Heart className={`h-3 w-3 transition-all duration-300 ${isTutorSaved(tutor.id) ? 'text-red-500 fill-current' : 'text-gray-400'}`} />
                            </Button>
                          </div>

                          {/* Local Services - Below Featured Badge */}
                          {hasLocalServices(tutor.id) && (
                            <div className="absolute top-10 left-2 z-10">
                              <div className="flex flex-col gap-1">
                                {getLocalServiceTypes(tutor.id).map((serviceType) => (
                                  <Badge 
                                    key={serviceType}
                                    className="text-xs px-2 py-1 bg-orange-500/90 text-white border-0 backdrop-blur shadow-sm"
                                  >
                                    <MapPin className="h-3 w-3 mr-1" />
                                    {serviceType}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Price - Top Left */}
                          <div className="absolute top-2 left-2 mt-8 z-10">
                            <div className="bg-white/90 backdrop-blur rounded-lg px-3 py-2 shadow-sm flex flex-col items-start gap-1">
                              {selectedCurrency.code === 'EGP' ? (
                                <div className="flex items-baseline gap-1" dir="rtl">
                                  <span className="text-sm font-bold text-green-600">
                                    {formatTutorPrice(tutor.hourlyRate || 0, tutor.pricingCurrency || 'USD', tutor).replace(selectedCurrency.symbol, '').trim()}
                                  </span>
                                  <span className="text-xs font-semibold text-green-600">{selectedCurrency.symbol}</span>
                                  <span className="text-xs text-green-500">/ ساعة</span>
                                </div>
                              ) : (
                                <div>
                                  <span className="text-sm font-bold text-green-600">
                                    {formatTutorPrice(tutor.hourlyRate || 0, tutor.pricingCurrency || 'USD', tutor)}
                                  </span>
                                  <span className="text-xs text-green-500">{getLocalizedText("/hr")}</span>
                                </div>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-5 px-1.5 text-[10px] py-0"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedTutorForPricing(tutor);
                                  setShowPricingModal(true);
                                }}
                              >
                                <Info className="h-2.5 w-2.5 mr-0.5" />
                                {selectedCurrency.code === 'EGP' ? 'خيارات أخرى للأسعار' : 'More'}
                              </Button>
                            </div>
                          </div>

                          {/* Bottom Content - Compact Layout */}
                          <div className="absolute bottom-0 left-0 right-0 p-3 text-white z-10">
                            {/* Name and Rating Row */}
                            <div className="flex items-center justify-between mb-2">
                              <h3 className="font-bold text-lg text-shadow cursor-pointer hover:text-blue-200 transition-colors flex-1 truncate mr-2"
                                  onClick={() => handleTutorClick(tutor)}>
                                {getTutorName(tutor)}
                              </h3>
                              
                                <div className="flex items-center gap-1 flex-shrink-0">
                                 <TutorRating tutorId={tutor.id} reviewStats={reviewStats} />
                               </div>
                            </div>

                            {/* Country and Session Preference Row */}
                            <div className="flex items-center justify-between mb-2">
                              <TutorCountryFlag 
                                countryCode={tutor.country}
                                showName={true}
                                size="sm"
                                className="bg-gray-100 backdrop-blur px-2 py-1 rounded text-gray-700 border border-gray-200 text-xs"
                              />
                              
                              {(() => {
                                const sessionInfo = getSessionPreferenceInfo(tutor.sessionPreference);
                                let bgColor = 'bg-blue-500/80';
                                if (tutor.sessionPreference === 'video_only' || tutor.sessionPreference === 'video') {
                                  bgColor = 'bg-green-500/80';
                                } else if (tutor.sessionPreference === 'chat_only' || tutor.sessionPreference === 'chat') {
                                  bgColor = 'bg-purple-500/80';
                                } else if (tutor.sessionPreference === 'both') {
                                  bgColor = 'bg-orange-500/80';
                                }
                                
                                return (
                                  <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${bgColor} backdrop-blur text-white border border-white/30`}>
                                    <sessionInfo.icon className="h-3 w-3" />
                                    <span className="hidden sm:inline">{sessionInfo.text}</span>
                                    <span className="sm:hidden">{sessionInfo.text.split(' ')[0]}</span>
                                  </div>
                                );
                              })()}
                            </div>
                            
                            {/* Subjects Row - Compact */}
                            <div className="flex flex-wrap gap-1">
                              {tutor.subjects?.slice(0, 2).map((subject, index) => (
                                <Badge key={index} className="text-xs px-2 py-1 bg-white/20 backdrop-blur border-white/30 text-white hover:bg-white/30">
                                  {subject.length > 8 ? subject.substring(0, 8) + '...' : subject}
                                </Badge>
                              ))}
                              {tutor.subjects && tutor.subjects.length > 2 && (
                                <Badge className="text-xs px-2 py-1 bg-white/20 backdrop-blur border-white/30 text-white">
                                  +{tutor.subjects.length - 2}
                                </Badge>
                              )}
                             </div>
                          </div>
                        </div>
                      </div>

                       {/* Bottom Content */}
                       <div className="p-4 flex-1 flex flex-col">
                         {/* Bio/Description Section */}
                         {(tutor.bio || tutor.description || tutor.heroIntroduction) && (
                           <div className="mb-4">
                             <p className="text-sm text-gray-600 line-clamp-3">
                               {tutor.heroIntroduction || tutor.bio || tutor.description}
                             </p>
                           </div>
                         )}
                         
                       {/* Action buttons - positioned at bottom with mt-auto */}
                      <div className="grid grid-cols-2 gap-2 mt-auto">
                        <Button 
                          size="sm"
                          className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white transition-all duration-300 hover:shadow-lg text-xs"
                          onClick={() => handleBookSession(tutor)}
                        >
                          <Calendar className="h-3 w-3 mr-1" />
                          Book Session
                        </Button>
                        
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="hover:bg-green-50 border-green-200 text-green-700 hover:border-green-300 transition-all duration-300 text-xs"
                          onClick={() => handleViewLessons(tutor)}
                        >
                          <BookOpen className="h-3 w-3 mr-1" />
                          Free Lessons
                        </Button>

                        <Button 
                          variant="outline" 
                          size="sm"
                          className="hover:bg-purple-50 border-purple-200 text-purple-600 hover:border-purple-300 transition-all duration-300 text-xs"
                          onClick={() => handleSendMessage(tutor)}
                        >
                          <MessageCircle className="h-3 w-3 mr-1" />
                          Message
                        </Button>

                        {/* Payment Methods Button */}
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-xs"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedTutorForPayment(tutor);
                            setShowPaymentModal(true);
                          }}
                        >
                          <Wallet className="h-3 w-3 mr-1" />
                          Payment
                        </Button>
                      </div>
                      </div>
                    </CardContent>
                   </Card>

                   );
                })}
              </div>
              
              {/* Infinite Scroll Trigger - only show when NOT in limited view */}
              {!shouldShowLimited && displayedCount < tutorsToShow.length && (
                <div ref={loadMoreRef} className="flex justify-center items-center py-8">
                  {isLoadingMore ? (
                    <div className="flex items-center gap-3">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      <span className="text-gray-600">Loading more tutors...</span>
                    </div>
                  ) : (
                    <Button
                      onClick={loadMore}
                      variant="outline"
                      size="lg"
                      className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50"
                    >
                      Load More Tutors ({tutorsToShow.length - displayedCount} remaining)
                    </Button>
                  )}
                </div>
              )}
              
              {/* Show "Load more" button if showing limited results */}
              {shouldShowLimited && tutorsToShow.length > 6 && (
                <div className="text-center mt-12">
                  <Button 
                    size="lg"
                    onClick={handleShowAllTutors}
                    className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-8 py-4 rounded-full text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                  >
                    Load more
                    <ArrowRight className="ml-3 h-6 w-6" />
                  </Button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-20">
              <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Search className="h-12 w-12 text-gray-400" />
              </div>
              <p className="text-2xl font-semibold text-gray-600 mb-4">No tutors found</p>
              <p className="text-gray-500 mb-8">Try adjusting your search filters or browse all tutors</p>
              <Button 
                variant="outline" 
                size="lg"
                onClick={handleClearFilters}
                className="px-8 py-3"
              >
                Clear Filters & Browse All
              </Button>
            </div>
          )}
        </div>
      </section>

      <Footer />

      <BookingModal
        isOpen={!!selectedTutorForBooking}
        onClose={() => setSelectedTutorForBooking(null)}
        tutor={selectedTutorForBooking}
      />

      <SendMessageModal
        isOpen={!!selectedTutorForMessage}
        onClose={() => setSelectedTutorForMessage(null)}
        tutor={selectedTutorForMessage}
      />

      <PaymentMethodsModal
        open={showPaymentModal}
        onOpenChange={setShowPaymentModal}
        tutorName={selectedTutorForPayment ? getTutorName(selectedTutorForPayment) : ''}
      />
      
      <TutorPricingModal
        isOpen={showPricingModal}
        onClose={() => {
          setShowPricingModal(false);
          setSelectedTutorForPricing(null);
        }}
        tutor={selectedTutorForPricing || {}}
      />
    </div>
  );
};

export default Tutors;
